import { AnimatePresence, motion, type Variants } from 'framer-motion'
import { useState, type CSSProperties, type ReactNode } from 'react'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { StatusChip } from '../Common'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'

// ============================================================================
// ChecklistPanel — the unified premium surface for every checklist details
// view across the app (Manage, Drawer, Mobile helper). Callers describe the
// sections they want; the shell handles the chrome (glass surface, aurora
// accent, animated section headers, collapse behavior, density) so every site
// gets the same look-and-feel with zero visual drift.
//
// Design decisions:
// - Sections are declarative (`ChecklistPanelSection`), so features can be
//   turned on/off per site without duplicating chrome.
// - Sections use framer-motion for enter/exit + collapse; the shell staggers
//   them by 24ms so the panel feels alive when it mounts.
// - `variant` controls page-vs-drawer-vs-mobile default density/max-width.
//   Callers may still override `density` explicitly.
// - `accent="aurora"` adds a subtle rotating gradient border. Off by default
//   on the mobile variant to keep animation cost down.
// ============================================================================

export type ChecklistPanelVariant = 'inline' | 'drawer' | 'mobile'
export type ChecklistPanelDensity = 'compact' | 'cozy' | 'spacious'

export type ChecklistPanelSection = {
  id: string
  title: string
  icon?: ReactNode
  chip?: ReactNode
  meta?: ReactNode
  body: ReactNode
  defaultOpen?: boolean
  /** Hide section entirely — cleaner than filtering in the parent. */
  hidden?: boolean
  /** Section stays open, no collapse control. */
  pinnedOpen?: boolean
  /** Extra class applied to the section wrapper (site-specific tuning). */
  className?: string
}

export type ChecklistPanelBanner = {
  tone?: 'info' | 'warning' | 'error' | 'success'
  icon?: ReactNode
  content: ReactNode
}

export type ChecklistPanelHeading = {
  eyebrow?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  chip?: ReactNode
  meta?: ReactNode
}

type Props = {
  variant?: ChecklistPanelVariant
  density?: ChecklistPanelDensity
  accent?: 'aurora' | 'solid' | 'none'
  heading?: ChecklistPanelHeading
  headerActions?: ReactNode
  banners?: ChecklistPanelBanner[]
  sections: ChecklistPanelSection[]
  footer?: ReactNode
  onClose?: () => void
  className?: string
  bodyClassName?: string
  style?: CSSProperties
}

const DEFAULT_DENSITY: Record<ChecklistPanelVariant, ChecklistPanelDensity> = {
  inline: 'cozy',
  drawer: 'cozy',
  mobile: 'compact',
}

const DEFAULT_ACCENT: Record<ChecklistPanelVariant, 'aurora' | 'solid' | 'none'> = {
  inline: 'aurora',
  drawer: 'aurora',
  mobile: 'solid',
}

const CHECKLIST_PANEL_EASE: [number, number, number, number] = [0.2, 0.7, 0.2, 1]

const panelVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.22, ease: CHECKLIST_PANEL_EASE },
  },
}

const sectionVariants: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.22, delay: index * 0.024, ease: CHECKLIST_PANEL_EASE },
  }),
}

export const ChecklistPanel = ({
  variant = 'inline',
  density,
  accent,
  heading,
  headerActions,
  banners,
  sections,
  footer,
  onClose,
  className,
  bodyClassName,
  style,
}: Props) => {
  const effectiveDensity = density ?? DEFAULT_DENSITY[variant]
  const effectiveAccent = accent ?? DEFAULT_ACCENT[variant]
  const visibleSections = sections.filter((section) => !section.hidden)

  return (
    <motion.section
      className={[
        'chkPanel',
        `chkPanel--${variant}`,
        `chkPanel--density-${effectiveDensity}`,
        effectiveAccent !== 'none' ? `chkPanel--accent-${effectiveAccent}` : '',
        className ?? '',
      ].filter(Boolean).join(' ')}
      style={style}
      variants={panelVariants}
      initial="hidden"
      animate="visible"
    >
      {effectiveAccent === 'aurora' && <span className="chkPanel__aurora" aria-hidden />}
      {effectiveAccent === 'solid' && <span className="chkPanel__solidAccent" aria-hidden />}

      {(heading || onClose || headerActions) && (
        <header className="chkPanel__header">
          {heading && (
            <div className="chkPanel__heading">
              {heading.eyebrow && <span className="chkPanel__eyebrow">{heading.eyebrow}</span>}
              <div className="chkPanel__titleRow">
                <h2 className="chkPanel__title">{heading.title}</h2>
                {heading.chip && <StatusChip size="sm" tone="neutral" className="chkPanel__titleChip">{heading.chip}</StatusChip>}
              </div>
              {heading.subtitle && <p className="chkPanel__subtitle">{heading.subtitle}</p>}
              {heading.meta && <div className="chkPanel__meta">{heading.meta}</div>}
            </div>
          )}
          <div className="chkPanel__headerActions">
            {headerActions}
            {onClose && (
              <IconButton
                variant="tertiary"
                size="sm"
                className="chkPanel__closeBtn"
                onClick={onClose}
                label="Close panel"
                icon={<CloseRoundedIcon fontSize="inherit" />}
              />
            )}
          </div>
        </header>
      )}

      {banners && banners.length > 0 && (
        <div className="chkPanel__banners">
          {banners.map((banner, index) => (
            <div
              key={`banner-${index}`}
              className={`chkPanel__banner chkPanel__banner--${banner.tone ?? 'info'}`}
              role="status"
            >
              {banner.icon && <span className="chkPanel__banner__icon">{banner.icon}</span>}
              <span className="chkPanel__banner__body">{banner.content}</span>
            </div>
          ))}
        </div>
      )}

      <div className={['chkPanel__body', bodyClassName ?? ''].filter(Boolean).join(' ')}>
        {visibleSections.map((section, index) => (
          <motion.div
            key={section.id}
            custom={index}
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
            className="chkPanel__sectionWrap"
          >
            <PanelSection section={section} />
          </motion.div>
        ))}
      </div>

      {footer && <footer className="chkPanel__footer">{footer}</footer>}
    </motion.section>
  )
}

const PanelSection = ({ section }: { section: ChecklistPanelSection }) => {
  const [open, setOpen] = useState(section.defaultOpen ?? section.pinnedOpen ?? false)
  const isOpen = section.pinnedOpen ? true : open
  return (
    <section className={['chkPanelSection', isOpen ? 'is-open' : '', section.className ?? ''].filter(Boolean).join(' ')}>
      <div className="chkPanelSection__border" aria-hidden />
      <Pressable
        className="chkPanelSection__header"
        onClick={() => { if (!section.pinnedOpen) setOpen((current) => !current) }}
        aria-expanded={isOpen}
        aria-controls={`chkSection-${section.id}`}
        disabled={section.pinnedOpen}
      >
        {!section.pinnedOpen && (
          <span className={`chkPanelSection__chevron${isOpen ? ' is-open' : ''}`} aria-hidden>
            <ChevronRightRoundedIcon fontSize="inherit" />
          </span>
        )}
        {section.icon && <span className="chkPanelSection__icon" aria-hidden>{section.icon}</span>}
        <span className="chkPanelSection__title">{section.title}</span>
        {section.meta && <span className="chkPanelSection__meta">{section.meta}</span>}
        {section.chip && <StatusChip size="sm" tone="neutral" className="chkPanelSection__chip">{section.chip}</StatusChip>}
      </Pressable>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={`chkSection-${section.id}`}
            className="chkPanelSection__body"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: [0.2, 0.7, 0.2, 1] }}
          >
            <div className="chkPanelSection__bodyInner">{section.body}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
